import { makeAutoObservable } from "mobx"

import Assignment from "./Assignment"
import AssignmentCollection from "../collections/AssignmentCollection"
import Package from "../../../models/Package"

type GenderString = string

export interface MismatchAlert {
  field: string
  expected: any
  actual: any
}

export interface MismatchAlertsByPassengerName {
  [passengerName: string]: MismatchAlert[]
}

export class Section {
  allAssignments: AssignmentCollection

  id: number
  number: number
  name: string
  notes: string
  capacity: number

  preferredGender?: GenderString
  preferredPackage?: Package

  constructor(allAssignments: AssignmentCollection, props: any) {
    makeAutoObservable(this, {}, { autoBind: true })

    this.allAssignments = allAssignments

    this.id = props.id
    this.number = props.number
    this.name = props.name
    this.notes = props.notes
    this.capacity = props.capacity
    this.preferredGender = props.preferredGender
    this.preferredPackage = props.preferredPackage
  }

  assignments: () => AssignmentCollection = () => {
    return this.allAssignments.filter((m: Assignment) => m.sectionId === this.id)
  }

  totalAssignedPassengers: () => number = () => {
    return this.assignments().length
  }

  isExactlyFull: () => boolean = () => {
    return this.capacity === this.assignments().length
  }

  availableSeats: () => number = () => {
    const takenSeats = this.assignments().length
    return Math.max(this.capacity - takenSeats, 0)
  }

  isOverbooked: () => boolean = () => {
    return this.capacity < this.assignments().length
  }

  allMismatchAlerts: () => MismatchAlertsByPassengerName | undefined = () => {
    let mismatchAlertsByPassengerName = {}
    for (let m of this.assignments().items) {
      const alerts = this.mismatchAlerts(m)
      if (alerts.length >= 1) {
        mismatchAlertsByPassengerName[m.passengerName] = alerts
      }
    }

    if (Object.entries(mismatchAlertsByPassengerName).length === 0) {
      return undefined
    } else {
      return mismatchAlertsByPassengerName
    }
  }

  mismatchAlerts: (Assignment) => MismatchAlert[] = (a: Assignment) => {
    let alerts: MismatchAlert[] = []

    if (this.preferredPackage) {
      if (a?.package?.id !== this.preferredPackage.id) {
        const packageAlert = {
          field: "package",
          expected: this.preferredPackage.name,
          actual: a?.package?.name,
        }
        alerts.push(packageAlert)
      }
    }

    if (this.preferredGender && a.gender !== this.preferredGender) {
      const packageAlert = { field: "gender", expected: this.preferredGender, actual: a.gender }
      alerts.push(packageAlert)
    }

    if (a.participation.cancelled == true) {
      const cancellationAlert = { field: "cancelled", expected: false, actual: true }
      alerts.push(cancellationAlert)
    }

    if (a.participation.waitlistStatus && a.participation.waitlistStatus.onWaitlist == true) {
      const onWaitlistAlert = { field: "on_waitlist", expected: false, actual: true }
      alerts.push(onWaitlistAlert)
    }

    return alerts
  }
}
